/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/ComponentRegistry';
import { page as SamplePage } from '../../help/sample_pages/Pulse';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './PulseTip';

const PulseTipManager: ComponentManagerFn<'PulseTip'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'PulseTip'>(platform, 'PulseTip'),
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    type: 'PulseTip',
    name: 'Pulse Tip',
    defaultValue,
    props: {
      tipText: {
        label: 'Tip Text',
        type: 'string',
      },
      hideDismiss: {
        label: 'Hide Dismiss',
        type: 'boolean',
      },
      hideMessageBox: {
        label: 'Hide Message Box',
        type: 'boolean',
      },
      top: {
        label: 'Top',
        type: 'string',
        category: 'Position',
      },
      right: {
        label: 'Right',
        type: 'string',
        category: 'Position',
      },
      bottom: {
        label: 'Bottom',
        type: 'string',
        category: 'Position',
      },
      left: {
        label: 'Left',
        type: 'string',
        category: 'Position',
      },
    },
    getDisplayText: (item) => '',
    category: 'Others',
    noOverlay: true,
    icon: { iconName: 'comment-alt-lines' },
    description: `**Overview**<br/>
The Pulse Tip is a user experience (UX) component designed to facilitate user onboarding and feature discovery. It attaches a small, animated "pulsing" beacon to a specific UI element (like a button, menu item, or map marker) to draw the user's attention.
Use this component to create "Guided Tours" for new users or to highlight recently added functionality within your application without cluttering the interface permanently.

**1. Content & Behavior**<br/>
* **Tip Text:** The message displayed inside the pop-up box associated with the pulse. This should be short and instructional (e.g., "Click here to save your changes").
* **Dismissal:** By default, the tip acts as a temporary overlay that disappears when the user acknowledges it.
    * **Hide Dismiss:** If set to **true**, it removes the "Dismiss" link/icon, making the tip more persistent or requiring a specific interaction to clear.
* **Visual Style:** * **Hide Message Box:** If set to **true**, the text box is hidden, leaving only the pulsing animation. This is ideal for subtly drawing attention to an element without obscuring adjacent content with a text bubble.

**2. Positioning**<br/>
The Pulse Tip is designed to anchor relative to its parent container. You can precisely place the tip using standard CSS-style offsets to ensure it doesn't overlap critical text.
* **Top / Right / Bottom / Left:** String values (e.g., '10px', '-5px', '50%').
* **Corner Attachment:** To make the pulse appear on the corner of a button (a common UI pattern), you often use negative values (e.g., **Top: -5px**, **Right: -5px**) to "badge" the target element.

**3. Interactivity**<br/>
* **TriggerOnClick:** The component supports a **TriggerOnClick** child component. 
* **Action:** While the default behavior is often informational, you can configure the pulse itself to trigger an action when clicked—such as opening a detailed help modal, launching a video tutorial, or navigating to a "What's New" page.

**4. Best Use Cases**<br/>
* **New Feature Rollout:** Attaching a pulse to a newly added button in a toolbar to inform existing users of its presence and function.
* **Onboarding Tours:** Guiding a new user through a complex workflow step-by-step (e.g., "First, select a date here to filter your records").
* **Contextual Help:** Marking complex input fields where users frequently get stuck, offering a helpful hint or validation rule explanation.
* **Map Annotations:** Used within Map components (like **Bing Map** or **IoT Image Mapper**) to highlight specific, urgent points of interest dynamically based on real-time data.`,
    helpRenders: [
      {
        title: 'Pulse Tip',
        description:
          'Below are some examples of showing pulse tips. Try changing the position of the tip.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 280,
        visibleProps: ['tipText', 'right', 'top'],
        item: {
          children: [],
          itemId: 'pulseTip',
          parent: 'button',
          props: {
            right: '-4px',
            tipText: 'Some information about this button...',
            top: '-4px',
          },
          type: 'PulseTip',
        },
      },
    ],
  };
};

export default PulseTipManager;
